import React, { useEffect } from 'react';
import cx from 'classnames';

import { Analytics } from '@trello/atlassian-analytics';
import { forTemplate, forNamespace } from 'app/common/lib/util/i18n';
import { Icon, IconName } from '@trello/app/common/components/controls';
import { UpgradePromptPill } from 'app/src/components/UpgradePrompts';
import { Tooltip } from '@trello/nachos';
import { dontUpsell } from '@trello/config';

const format = forTemplate('board-views');
const formatPrompt = forNamespace('upgrade prompt');

export interface BoardViewsPopoverProps {
  idBoard: string;
  orgName?: string;
  currentView: 'list' | 'calendar' | 'map';
  isCalendarPowerUpEnabled: boolean;
  isMapPowerUpEnabled: boolean;
  hasReachedPowerUpLimit: boolean;
  showCalendarOption?: boolean;
  showMapOption?: boolean;
  navigateToListView: () => void;
  navigateToCalendarView: () => void;
  navigateToMapView: () => void;
}

interface TooltipListItemProps {
  showTooltip: boolean;
  children: React.ReactNode;
}

const TooltipListItem: React.FunctionComponent<TooltipListItemProps> = ({
  showTooltip,
  children,
}) => {
  if (showTooltip) {
    return (
      <Tooltip tag="li" content={format('limit-upsell')} delay={100}>
        {children}
      </Tooltip>
    );
  }

  return <li>{children}</li>;
};

export const BoardViewsPopover: React.FunctionComponent<BoardViewsPopoverProps> = ({
  idBoard,
  orgName,
  currentView,
  isCalendarPowerUpEnabled,
  isMapPowerUpEnabled,
  hasReachedPowerUpLimit,
  showCalendarOption,
  showMapOption,
  navigateToListView,
  navigateToCalendarView,
  navigateToMapView,
}) => {
  useEffect(() => {
    Analytics.sendScreenEvent({
      name: 'boardViewsInlineDialog',
      containers: {
        board: {
          id: idBoard,
        },
      },
    });
  }, [idBoard]);

  const showUpsellForCalendar =
    hasReachedPowerUpLimit && !isCalendarPowerUpEnabled && !dontUpsell;
  const showUpsellForMap =
    hasReachedPowerUpLimit && !isMapPowerUpEnabled && !dontUpsell;

  const ctaLink = orgName ? `/${orgName}/billing` : '/select-team-to-upgrade';
  const upsellPill = (
    <UpgradePromptPill
      ctaLink={ctaLink}
      iconName={IconName.BusinessClass}
      cta={formatPrompt('upgrade team')}
      ctaOnClick={() => {
        Analytics.sendUIEvent({
          action: 'clicked',
          actionSubject: 'pill',
          actionSubjectId: 'bcUpgradePill',
          source: 'boardViewsInlineDialog',
          containers: {
            board: {
              id: idBoard,
            },
          },
        });
      }}
    />
  );

  return (
    <ul className="pop-over-list">
      <TooltipListItem showTooltip={false}>
        <div
          className={cx({
            'pop-over-list-item': true,
            current: currentView === 'list',
          })}
          role="button"
          onClick={() => {
            Analytics.sendUIEvent({
              action: 'clicked',
              actionSubject: 'dropdownItem',
              actionSubjectId: 'listViewDropdownItem',
              source: 'boardViewsInlineDialog',
              containers: {
                board: {
                  id: idBoard,
                },
              },
            });

            navigateToListView();
          }}
        >
          <Icon name={IconName.List} small /> {format('list')}
          <span className="sub-name">{format('board-description')}</span>
        </div>
      </TooltipListItem>
      {showCalendarOption && (
        <TooltipListItem showTooltip={showUpsellForCalendar}>
          <div
            className={cx({
              'pop-over-list-item': true,
              current: currentView === 'calendar',
              disabled: showUpsellForCalendar,
            })}
            role="button"
            onClick={() => {
              if (showUpsellForCalendar) {
                return;
              }

              Analytics.sendUIEvent({
                action: 'clicked',
                actionSubject: 'dropdownItem',
                actionSubjectId: 'calendarViewDropdownItem',
                source: 'boardViewsInlineDialog',
                containers: {
                  board: {
                    id: idBoard,
                  },
                },
              });

              navigateToCalendarView();
            }}
          >
            <Icon name={IconName.Calendar} small /> {format('calendar')}{' '}
            {showUpsellForCalendar && upsellPill}
            <span className="sub-name">{format('calendar-description')}</span>
          </div>
        </TooltipListItem>
      )}
      {showMapOption && (
        <TooltipListItem showTooltip={showUpsellForMap}>
          <div
            className={cx({
              'pop-over-list-item': true,
              current: currentView === 'map',
              disabled: showUpsellForMap,
            })}
            role="button"
            onClick={() => {
              if (showUpsellForMap) {
                return;
              }

              Analytics.sendUIEvent({
                action: 'clicked',
                actionSubject: 'dropdownItem',
                actionSubjectId: 'mapViewDropdownItem',
                source: 'boardViewsInlineDialog',
                containers: {
                  board: {
                    id: idBoard,
                  },
                },
              });

              navigateToMapView();
            }}
          >
            <Icon name={IconName.Location} small /> {format('map')}{' '}
            {showUpsellForMap && upsellPill}
            <span className="sub-name">{format('map-description')}</span>
          </div>
        </TooltipListItem>
      )}
    </ul>
  );
};
